/**
 * Helpers do portal do aluno — portados de `frontend/components/portal/utils.ts`
 * (legado Next), adaptados ao app novo (Vite):
 *  - `gifPublicUrl` lê o project URL de `env.supabaseUrl` (Vite) em vez de
 *    `process.env.NEXT_PUBLIC_SUPABASE_URL`.
 *  - O parser de `detail` e os formatadores são portados inline (o legado
 *    importava de `@/lib/workout-detail-parser`, que não existe aqui).
 *
 * São funções puras — sem React — pra ficarem testáveis e reutilizáveis entre
 * HomeView e TreinosView.
 */

import { env } from "@/lib/env";
import type {
  StudentExecution,
  StudentWorkout,
  StudentWorkoutItem,
} from "@/lib/types";

// ─── GIF URL ─────────────────────────────────────────────────────────────────

/**
 * Constrói URL pública pra GIF de exercício no Supabase Storage.
 * Coexistem 2 buckets: `gifs` (novo, FLAT) e `exercicios` (legado, com subpasta
 * por grupo muscular). Auto-detecta pelo `/` no path. URL absoluta volta como veio.
 */
export function gifPublicUrl(gifPath: string | null | undefined): string | null {
  if (!gifPath) return null;
  if (/^https?:\/\//i.test(gifPath)) return gifPath;
  const base = env.supabaseUrl.replace(/\/$/, "");
  const bucket = gifPath.includes("/") ? "exercicios" : "gifs";
  return `${base}/storage/v1/object/public/${bucket}/${gifPath
    .split("/")
    .map(encodeURIComponent)
    .join("/")}`;
}

// ─── Parser tolerante do campo `detail` ──────────────────────────────────────

interface ParsedWorkoutDetail {
  sets: number | null;
  reps: string | null;
  load_kg: number | null;
  rest_seconds: number | null;
}

const SETS_REGEX = /^\s*(\d+)x([\d-]+|\?)\s*$/i;
const LOAD_REGEX = /^\s*@\s*([\d.,]+)\s*kg\s*$/i;
const REST_REGEX = /^\s*descanso\s+(\d+)\s*s\s*$/i;

const INLINE_SETS_REGEX = /(\d+)\s*[xX×]\s*([\d-]+|\?)/;
const INLINE_LOAD_REGEX = /@\s*([\d.,]+)\s*kg|(\d[\d.,]*)\s*kg(?!\w)/i;
const INLINE_REST_REGEX = /descanso\s+(\d+)\s*s|(\d+)\s*s\s*(?:de\s+)?(?:descanso|rest)/i;

/**
 * Parseia um `detail` livre num shape estruturado. Tolerante a ordem, campos
 * ausentes e formato fused (ex: "4x8-10 @ 50kg | descanso 90s").
 */
function parseWorkoutDetail(detail: string | null | undefined): ParsedWorkoutDetail {
  const out: ParsedWorkoutDetail = {
    sets: null,
    reps: null,
    load_kg: null,
    rest_seconds: null,
  };
  const raw = detail ?? "";
  if (!raw.trim()) return out;

  const parts = raw
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);

  for (const part of parts) {
    const setsMatch = SETS_REGEX.exec(part);
    if (setsMatch) {
      const sets = parseInt(setsMatch[1], 10);
      out.sets = Number.isFinite(sets) ? sets : null;
      out.reps = setsMatch[2] === "?" ? null : setsMatch[2];
      continue;
    }
    const loadMatch = LOAD_REGEX.exec(part);
    if (loadMatch) {
      const load = parseFloat(loadMatch[1].replace(",", "."));
      out.load_kg = Number.isFinite(load) ? load : null;
      continue;
    }
    const restMatch = REST_REGEX.exec(part);
    if (restMatch) {
      const rest = parseInt(restMatch[1], 10);
      out.rest_seconds = Number.isFinite(rest) ? rest : null;
      continue;
    }
    consumeInline(part, out);
  }

  return out;
}

/** Extrai inline sets/load/rest de um chunk fused (sem pipe separator). */
function consumeInline(part: string, out: ParsedWorkoutDetail): void {
  let remaining = part;

  if (out.rest_seconds == null) {
    const m = INLINE_REST_REGEX.exec(remaining);
    if (m) {
      const n = parseInt(m[1] ?? m[2], 10);
      if (Number.isFinite(n)) out.rest_seconds = n;
      remaining = (remaining.slice(0, m.index) + remaining.slice(m.index + m[0].length)).trim();
    }
  }

  if (out.load_kg == null) {
    const m = INLINE_LOAD_REGEX.exec(remaining);
    if (m) {
      const rawLoad = (m[1] ?? m[2] ?? "").replace(",", ".");
      const n = parseFloat(rawLoad);
      if (Number.isFinite(n)) out.load_kg = n;
      remaining = (remaining.slice(0, m.index) + remaining.slice(m.index + m[0].length)).trim();
    }
  }

  if (out.sets == null && out.reps == null) {
    const m = INLINE_SETS_REGEX.exec(remaining);
    if (m) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n)) out.sets = n;
      out.reps = m[2] === "?" ? null : m[2];
    }
  }
}

/** Formata séries × reps numa label curta. */
function formatSetsReps(sets: number | null, reps: string | null): string | null {
  if (sets != null && reps) {
    return `${sets} ${sets === 1 ? "série" : "séries"} × ${reps} reps`;
  }
  if (sets != null) return `${sets} ${sets === 1 ? "série" : "séries"}`;
  if (reps) return `${reps} reps`;
  return null;
}

/** Formata carga: "50 kg", "12.5 kg". */
function formatLoad(loadKg: number | null): string | null {
  if (loadKg == null || loadKg <= 0) return null;
  const formatted = Number.isInteger(loadKg)
    ? loadKg.toString()
    : loadKg.toFixed(1).replace(/\.0$/, "");
  return `${formatted} kg`;
}

// ─── Resumo de prescrição pra preview de card ────────────────────────────────

export interface PrescriptionSummary {
  detail: string | null;
  loadLabel: string | null;
  restSeconds: number | null;
}

export function summarizePrescription(item: StudentWorkoutItem): PrescriptionSummary {
  const parsed = parseWorkoutDetail(item.detail);
  return {
    detail: formatSetsReps(parsed.sets, parsed.reps),
    loadLabel: formatLoad(parsed.load_kg),
    restSeconds: parsed.rest_seconds,
  };
}

// ─── Agregações de workout / execução ────────────────────────────────────────

/** Conta total de exercícios de um workout (soma items de cada block). */
export function countWorkoutItems(workout: StudentWorkout): number {
  return workout.blocks.reduce((acc, b) => acc + b.items.length, 0);
}

/** Lista os itens de um workout como array plano (ignora blocks). */
export function flattenWorkoutItems(workout: StudentWorkout): StudentWorkoutItem[] {
  return workout.blocks.flatMap((b) => b.items);
}

/** Formata uma data ISO como rótulo curto em pt-BR ("hoje", "ontem", "12 mai"). */
export function shortDateLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const oneDay = 1000 * 60 * 60 * 24;
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startD = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startToday - startD) / oneDay);
    if (diffDays === 0) return "hoje";
    if (diffDays === 1) return "ontem";
    if (diffDays > 1 && diffDays < 7) return `há ${diffDays} dias`;
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch {
    return null;
  }
}

/**
 * Última execução `completed` de cada workout
 * (mapa workout_id → ISO da execução mais recente).
 */
export function lastExecutionByWorkout(
  executions: StudentExecution[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const e of executions) {
    if (e.status !== "completed") continue;
    const current = map.get(e.workout_id);
    if (!current || Date.parse(e.executed_at) > Date.parse(current)) {
      map.set(e.workout_id, e.executed_at);
    }
  }
  return map;
}

/**
 * Heurística pra escolher o "treino de hoje": sugere o próximo na rotação
 * (sort_order) após o último completed; senão o primeiro do plano. Só sugestão
 * de UX — o aluno pode iniciar qualquer treino livremente.
 */
export function suggestTodaysWorkout(
  workouts: StudentWorkout[],
  executions: StudentExecution[],
): StudentWorkout | null {
  if (workouts.length === 0) return null;
  if (workouts.length === 1) return workouts[0];

  const lastCompleted = [...executions]
    .filter((e) => e.status === "completed")
    .sort((a, b) => Date.parse(b.executed_at) - Date.parse(a.executed_at))[0];

  const sorted = [...workouts].sort((a, b) => a.sort_order - b.sort_order);
  if (!lastCompleted) return sorted[0];

  const idx = sorted.findIndex((w) => w.id === lastCompleted.workout_id);
  if (idx === -1) return sorted[0];
  return sorted[(idx + 1) % sorted.length];
}

/** Id do workout sugerido pra hoje (usado pra destacar o card na TreinosView). */
export function highlightWorkoutId(
  workouts: StudentWorkout[],
  executions: StudentExecution[],
): string | null {
  return suggestTodaysWorkout(workouts, executions)?.id ?? null;
}

// ─── Snapshot semanal + streak ───────────────────────────────────────────────

export interface WeeklySnapshot {
  count: number;
  /** 7 posições (seg..dom) — true se houve treino completo no dia. */
  weekDots: boolean[];
  streakDays: number;
}

/**
 * Treinos concluídos na semana atual (segunda a domingo) + streak de dias
 * consecutivos partindo de hoje pra trás.
 */
export function weeklySnapshot(executions: StudentExecution[]): WeeklySnapshot {
  const now = new Date();
  // Segunda-feira como início da semana — Date.getDay() retorna 0=Dom..6=Sab.
  const dayOfWeek = (now.getDay() + 6) % 7; // 0=Seg..6=Dom
  const startOfWeek = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - dayOfWeek,
  );
  const startOfWeekMs = startOfWeek.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const weekDots: boolean[] = [false, false, false, false, false, false, false];

  const completed = executions.filter((e) => e.status === "completed");
  let count = 0;
  for (const e of completed) {
    const ts = Date.parse(e.executed_at);
    if (!Number.isFinite(ts)) continue;
    const diffDays = Math.floor((ts - startOfWeekMs) / oneDay);
    if (diffDays >= 0 && diffDays < 7) {
      weekDots[diffDays] = true;
      count += 1;
    }
  }

  // Streak: dias consecutivos com treino completo partindo de hoje pra trás.
  const completedDays = new Set<string>();
  for (const e of completed) {
    const d = new Date(e.executed_at);
    if (Number.isNaN(d.getTime())) continue;
    completedDays.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }
  let streak = 0;
  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  while (true) {
    const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
    if (completedDays.has(key)) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }

  return { count, weekDots, streakDays: streak };
}

/** Conta total de itens de uma dieta (soma items de cada block). */
export function countDietItems(blocks: { items: unknown[] }[]): number {
  return blocks.reduce((acc, b) => acc + b.items.length, 0);
}
